import { Link } from 'wouter'
import type { CelestialBody } from '../lore'
import { lorebookHref } from '../lore'
import { Markdown } from './Markdown'
import { BodyExtras } from './BodyExtras'

const typeLabel: Record<string, string> = {
  star: 'Star',
  molten: 'Molten core world',
  rocky: 'Rocky planet',
  terrestrial: 'Terrestrial',
  'gas-giant': 'Gas giant',
  frozen: 'Frozen world',
  dwarf: 'Dwarf moon',
  debris: 'Debris field',
  'asteroid-belt': 'Asteroid belt',
}

/** Slide-in detail panel shown when a body is selected on the map. */
export function LorePanel({ body, onClose }: { body: CelestialBody; onClose: () => void }) {
  return (
    <aside className="absolute right-0 top-0 z-30 flex h-full w-full max-w-md flex-col border-l border-edge bg-void-2/95 shadow-2xl backdrop-blur-xl motion-safe:animate-in">
      <div className="flex items-start justify-between gap-3 border-b border-edge p-5">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.3em] text-signal/70">
            {typeLabel[body.type] ?? body.type}
          </div>
          <h2 className="mt-1 font-display text-2xl font-semibold text-white">{body.name}</h2>
          {body.tagline && <p className="mt-1 text-sm text-slate-400">{body.tagline}</p>}
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          className="shrink-0 rounded-full border border-edge px-3 py-1 text-sm text-slate-300 transition hover:border-sos/60 hover:text-white"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        <div className="md">
          <Markdown>{body.lore}</Markdown>
        </div>

        <BodyExtras body={body} />

        {body.wip && (
          <div className="mt-6 rounded-lg border border-dashed border-edge p-3 text-xs text-slate-500">
            Sections of this entry are still being written. Check back as the lorebook expands.
          </div>
        )}

        <div className="mt-6">
          <Link href={lorebookHref(body)} className="text-sm text-signal hover:underline">
            Open full entry →
          </Link>
        </div>
      </div>
    </aside>
  )
}
